import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { SpecialPaymentSeason } from '@coin/shared/season-mgmt/util';
import { HttpHelpersService, LoadingService } from '@coin/shared/core/data-access';
import { SeasonTypeEnum, SpecialPaymentProposalState } from '@coin/shared/core/util-enums';
import { environment } from '@coin/shared/core/util-environments';
import {
  BenchmarkDto,
  EmployeeWithCompensation,
  MassExportTransactionStatus,
  PaginatedResult,
  SearchItemService,
  TransactionStatus
} from '@coin/shared/core/util-models';
import { ToastrService } from 'ngx-toastr';
import { Observable, of } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
import { SpecialPaymentCandidate } from '../models/special-payment-candidate.model';
import { SpecialPaymentDashboard } from '../models/special-payment-dashboard.model';
import { SpecialPaymentProposal } from '../models/special-payment-proposal.model';
import { SpecialPaymentCompensationTypeEligibility } from '../models/special-payment-compensation-type-eligibility.model';

@Injectable({
  providedIn: 'root'
})
export class SpecialPaymentService implements SearchItemService {
  private httpClient = inject(HttpClient);
  private httpHelpersService = inject(HttpHelpersService);
  private loadingService = inject(LoadingService);
  private toastrService = inject(ToastrService);

  public getSeasons(employeeId: string): Observable<SpecialPaymentSeason[]> {
    // TODO remove seasonType in frontend and backend, as it will always be the same
    const params = new HttpParams({
      fromObject: {
        employeeId,
        seasonType: SeasonTypeEnum.SpecialPaymentCash,
        isEligible: true
      }
    });

    return this.httpClient
      .get<SpecialPaymentSeason[]>(`${environment.api.baseUrl}/admin/api/v3/master/seasons/special-payment`, { params })
      .pipe(
        map(seasons => seasons.map(season => ({ ...season, plan: season?.plans[0] ?? null }))), // here only one plan is returned - always the eligible
        catchError(() => of(null))
      );
  }

  public getSearchItemsByText(
    text: string,
    page: number,
    seasonId?: string,
    searchSuffix?: string
  ): Observable<SpecialPaymentCandidate[]> {
    const encodedText = encodeURIComponent(text);
    let queryString = '';
    if (encodedText) {
      queryString = `&Query.gid=${encodedText}&Query.firstName=${encodedText}&Query.lastName=${encodedText}&Query.orgCode=${encodedText}`;
    }
    return this.httpClient
      .get<
        PaginatedResult<SpecialPaymentCandidate>
      >(`${environment.api.baseUrl}/customer/api/v1/master/special-payment/candidates?Paging.page=${page + 1}&Paging.size=50${queryString}${searchSuffix ?? ''}`)
      .pipe(
        map(result => result.content),
        this.httpHelpersService.handleError('Cannot get search item')
      );
  }

  public getEmployeeDetails(id: string): Observable<EmployeeWithCompensation> {
    return this.httpClient
      .get<EmployeeWithCompensation>(
        `${environment.api.baseUrl}/customer/api/v1/master/employees/${id}?seasonType=${SeasonTypeEnum.SpecialPaymentCash}`
      )
      .pipe(this.httpHelpersService.handleError('Cannot get employee detail data'));
  }

  public getEmployeeBenchmarks(id: string): Observable<BenchmarkDto[]> {
    return this.httpClient
      .get<
        BenchmarkDto[]
      >(`${environment.api.baseUrl}/customer/api/v1/master/employees/${id}/benchmarks?seasonType=${SeasonTypeEnum.SpecialPaymentCash}`)
      .pipe(this.httpHelpersService.handleError('Cannot get employee detail data'));
  }

  public getSpecialPaymentProposal(id: string): Observable<SpecialPaymentProposal> {
    return this.httpClient
      .get<SpecialPaymentProposal>(`${environment.api.baseUrl}/customer/api/v1/master/records/special-payment/${id}`)
      .pipe(this.httpHelpersService.handleError('Cannot load proposal'));
  }

  public getSpecialPaymentTaskProposals(
    page: number,
    size: number,
    isCurrentApprover?: boolean,
    state?: SpecialPaymentProposalState,
    filterAndSortQuery = '',
    allProposals = false
  ): Observable<PaginatedResult<SpecialPaymentProposal>> {
    let query = `?Paging.page=${page + 1}&Paging.size=${size}`;

    if (allProposals) {
      query += '&allProposals=true';
    }

    if (isCurrentApprover !== undefined) {
      query += `&Query.isCurrentApprover=${isCurrentApprover}`;
    }

    if (state) {
      query += `&Query.ProposalState=${state}`;
    }

    return this.httpClient
      .get<
        PaginatedResult<SpecialPaymentProposal>
      >(`${environment.api.baseUrl}/customer/api/v1/master/records/special-payment${query}${filterAndSortQuery}`)
      .pipe(this.httpHelpersService.handleError('Cannot load proposals'));
  }

  public approveOrReject(
    task: SpecialPaymentProposal,
    state: SpecialPaymentProposalState
  ): Observable<SpecialPaymentProposal> {
    return this.loadingService.withLoadingScreen(
      this.httpClient
        .post<SpecialPaymentProposal>(
          `${environment.api.baseUrl}/customer/api/v1/master/records/special-payment/${task.id}/acknowledgement`,
          {
            state
          }
        )
        .pipe(
          tap(() => this.toastrService.success('Updated.')),
          this.httpHelpersService.handleError('Cannot approve or reject proposal')
        )
    );
  }

  public resetProposalRequest(proposalId: string, steps: number): Observable<SpecialPaymentProposal> {
    return this.loadingService.withLoadingScreen(
      this.httpClient
        .post<SpecialPaymentProposal>(
          `${environment.api.baseUrl}/customer/api/v1/master/records/special-payment/${proposalId}/reset`,
          {
            steps
          }
        )
        .pipe(
          tap(() => this.toastrService.success('Resetted proposal.')),
          this.httpHelpersService.handleError('Cannot reset proposal')
        )
    );
  }

  public createProposal(proposal: SpecialPaymentProposal): Observable<SpecialPaymentProposal> {
    return this.loadingService.withLoadingScreen(
      this.httpClient
        .post<SpecialPaymentProposal>(
          `${environment.api.baseUrl}/customer/api/v1/master/records/special-payment`,
          proposal
        )
        .pipe(
          tap(() => this.toastrService.success('Created new proposal.')),
          this.httpHelpersService.handleError('Cannot create proposal')
        )
    );
  }

  public updateProposal(proposal: SpecialPaymentProposal): Observable<SpecialPaymentProposal> {
    return this.loadingService.withLoadingScreen(
      this.httpClient
        .patch<SpecialPaymentProposal>(
          `${environment.api.baseUrl}/customer/api/v1/master/records/special-payment/${proposal.id}`,
          proposal
        )
        .pipe(
          tap(() => this.toastrService.success('Updated proposal.')),
          this.httpHelpersService.handleError('Cannot update proposal')
        )
    );
  }

  public deleteProposal(proposalId: string): Observable<void> {
    return this.loadingService.withLoadingScreen(
      this.httpClient
        .delete<void>(`${environment.api.baseUrl}/customer/api/v1/master/records/special-payment/${proposalId}`)
        .pipe(
          tap(() => this.toastrService.success('Deleted proposal.')),
          this.httpHelpersService.handleError('Cannot delete proposal')
        )
    );
  }

  public getSpecialPaymentHistory(employeeId: string): Observable<SpecialPaymentProposal[]> {
    const params = new HttpParams()
      .append('Query.EmployeeId', employeeId)
      .append('Query.ProposalState', SpecialPaymentProposalState.AutoApproved)
      .append('Query.ProposalState', SpecialPaymentProposalState.Approved)
      .append('Query.ExportState', 'Done')
      .append('Query.IsEvaluationSeason', 'true');

    return this.loadingService.withLoadingScreen(
      this.httpClient
        .get<
          PaginatedResult<SpecialPaymentProposal>
        >(`${environment.api.baseUrl}/customer/api/v1/master/records/special-payment`, { params })
        .pipe(
          this.httpHelpersService.handleError('Cannot get proposal history.'),
          map(result => result.content)
        )
    );
  }

  public getCompensationTypeEligibility(
    employeeId: string,
    seasonId: string
  ): Observable<SpecialPaymentCompensationTypeEligibility> {
    const params = new HttpParams({
      fromObject: {
        employeeId,
        seasonId
      }
    });

    return this.httpClient
      .get<SpecialPaymentCompensationTypeEligibility>(
        `${environment.api.baseUrl}/customer/api/v1/master/records/special-payment/compensation-eligibility`,
        { params }
      )
      .pipe(this.httpHelpersService.handleError('Cannot validate compensation type eligibility.'));
  }

  public getDashboard(allProposals?: boolean): Observable<SpecialPaymentDashboard> {
    let params = new HttpParams();
    if (allProposals) {
      params = params.set('allProposals', true);
    }

    return this.loadingService.withLoadingScreen(
      this.httpClient
        .get<SpecialPaymentDashboard>(`${environment.api.baseUrl}/customer/api/v1/master/special-payment/dashboard`, {
          params
        })
        .pipe(this.httpHelpersService.handleError('Cannot load dashboard'))
    );
  }

  public getFieldValues(
    page: number,
    size: number,
    key: string,
    searchText = '',
    sessionId?: string,
    parentOrgCode?: string,
    searchSuffix = ''
  ): Observable<PaginatedResult<string>> {
    const searchQuery = searchText?.trim() ? `&Query.${key}=${encodeURIComponent(searchText)}` : '';

    return this.httpClient
      .get<
        PaginatedResult<string>
      >(`${environment.api.baseUrl}/customer/api/v1/master/records/special-payment/field-values?Paging.page=${page + 1}&Paging.size=${size}&targetProperty=${key}${searchQuery}${searchSuffix}`)
      .pipe(catchError(() => of(null)));
  }

  public startExcelExport(
    fileName: string,
    filterQuery: string
  ): Observable<TransactionStatus<MassExportTransactionStatus>> {
    return this.httpClient
      .post<
        TransactionStatus<MassExportTransactionStatus>
      >(`${environment.api.baseUrl}/customer/api/v1/master/records/special-payment/export${filterQuery}`, { fileName })
      .pipe(
        tap(() => this.toastrService.success('Exported to Excel.')),
        this.loadingService.withLoadingScreen,
        this.httpHelpersService.handleError('Cannot export to Excel')
      );
  }
}
